import React, { useEffect, useState } from 'react';
import { Info } from 'lucide-react';

interface BuildInfo {
  app: string;
  version: string;
  build_id: string;
  git_sha?: string | null;
  git_commit_time?: string | null;
  started_at?: string;
  environment?: string;
}

/**
 * VersionBadge — small footer badge that surfaces the running build
 * of the Nexus backend (v14+).
 *
 * Why: the class of "I deployed but I don't see the changes" bugs
 * happens when a stale build lands in production silently. The badge
 * gives every user a deterministic way to verify which commit is
 * live: click it to reveal the git SHA, build id, and start time.
 *
 * The badge is intentionally low-key (opacity 40 %) so it never
 * distracts, but it's always visible in the corner. Hovering /
 * tapping expands to reveal details.
 */
export const VersionBadge: React.FC = () => {
  const [info, setInfo] = useState<BuildInfo | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${base}/api/version`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled && data) setInfo(data); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  if (!info) return null;
  const shaRaw = info.build_id && info.build_id !== 'unknown'
    ? info.build_id
    : (info.git_sha || '');
  const sha = shaRaw ? shaRaw.slice(0, 8) : '';
  const label = info.version && info.version !== 'dev'
    ? info.version
    : `dev·${sha || '?'}`;

  const commitTime = info.git_commit_time
    ? new Date(info.git_commit_time).toLocaleString()
    : null;

  return (
    <button
      type="button"
      onClick={() => setExpanded((x) => !x)}
      data-testid="nexus-version-badge"
      className="fixed bottom-2 left-2 z-40 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-mono tracking-wide transition"
      style={{
        background: 'hsl(var(--bg-elev) / 0.7)',
        border: '1px solid hsl(var(--border))',
        color: 'hsl(var(--text-muted))',
        backdropFilter: 'blur(6px)',
        opacity: expanded ? 1 : 0.35,
        // Sprint Cierre Final v1.27 · movido a bottom-LEFT para que no
        // colisione con el botón Enviar del chat (que está en
        // bottom-right en móvil). También ligeramente más
        // transparente para no distraer.
      }}
      title={`Nexus ${info.version} · ${sha || 'no-sha'}`}
    >
      <Info className="w-2.5 h-2.5" />
      <span>{label}</span>
      {sha && <span className="opacity-60">·{sha}</span>}
      {expanded && (
        <span
          className="ml-2 pl-2 border-l text-[10px]"
          style={{ borderColor: 'hsl(var(--border))', color: 'hsl(var(--text))' }}
        >
          {commitTime && (
            <span className="opacity-70">{commitTime}</span>
          )}
          {info.environment && (
            <span
              className="ml-2 uppercase text-[9px] px-1.5 py-[1px] rounded-full"
              style={{
                background: info.environment === 'production'
                  ? 'hsl(150 55% 25% / 0.5)'
                  : 'hsl(220 55% 25% / 0.5)',
                color: info.environment === 'production'
                  ? 'hsl(150 65% 75%)'
                  : 'hsl(220 65% 75%)',
              }}
            >
              {info.environment}
            </span>
          )}
        </span>
      )}
    </button>
  );
};

export default VersionBadge;
